import { CsvRow, ImportPlan, ImportDienstleister } from './types';

export function parseObjektnummer(beschreibung: string): string | null {
  const m = (beschreibung || '').trim().match(/^(\d+(?:\.\d+)?)\b/);
  return m ? m[1] : null;
}

export function normalizeName(name: string): string {
  return (name || '').trim().replace(/[,\s]+$/, '').trim().toLowerCase();
}

export function dedupKey(name: string, email?: string | null): string {
  return `${normalizeName(name)}|${(email || '').trim().toLowerCase()}`;
}

export function splitKategorien(roh: string): string[] {
  const parts = (roh || '').split(',').map(s => s.trim()).filter(Boolean);
  return parts.length > 0 ? parts : ['Sonstige Dienstleister'];
}

function leerZuNull(v: string): string | null {
  const t = (v || '').trim();
  return t === '' ? null : t;
}

// CSV mit Semikolon-Trenner + "..."-Quoting (deutsches Export-Format).
export function parseCsv(text: string): CsvRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length <= 1) return [];
  const rows: CsvRow[] = [];
  // Header-Reihenfolge der gelieferten Datei ist fix; wir mappen per Index.
  for (let i = 1; i < lines.length; i++) {
    const c = splitSemicolon(lines[i]);
    rows.push({
      beschreibung: c[1] || '',
      name: c[2] || '',
      kategorien: c[3] || '',
      von: c[4] || '',
      bis: c[5] || '',
      strasse: c[6] || '',
      plz: c[7] || '',
      stadt: c[8] || '',
      ansprechpartner: c[9] || '',
      email: c[10] || '',
      telefon: c[11] || '',
      vertragsart: c[13] || '',
    });
  }
  return rows;
}

function splitSemicolon(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQuotes = !inQuotes; continue; }
    if (ch === ';' && !inQuotes) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

function deDateToIso(d: string): string | null {
  const t = (d || '').trim();
  const m = t.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
}

export function buildImportPlan(rows: CsvRow[], objektByNummer: Record<string, string>): ImportPlan {
  const byKey = new Map<string, ImportDienstleister>();
  const ohneObjektMatch: { name: string; beschreibung: string }[] = [];

  for (const r of rows) {
    const nummer = parseObjektnummer(r.beschreibung);
    const objektId = nummer ? objektByNummer[nummer] : undefined;
    if (!objektId) {
      ohneObjektMatch.push({ name: r.name, beschreibung: r.beschreibung });
      continue;
    }
    const key = dedupKey(r.name, r.email);
    let dl = byKey.get(key);
    if (!dl) {
      dl = {
        name: (r.name || '').trim().replace(/[,\s]+$/, ''),
        ansprechpartner: leerZuNull(r.ansprechpartner),
        email: leerZuNull(r.email),
        telefon: leerZuNull(r.telefon),
        strasse: leerZuNull(r.strasse),
        plz: leerZuNull(r.plz),
        stadt: leerZuNull(r.stadt),
        kategorien: [],
        objektLinks: [],
      };
      byKey.set(key, dl);
    }
    // Stammdaten vom ersten gefuellten Vorkommen ergaenzen
    dl.ansprechpartner ??= leerZuNull(r.ansprechpartner);
    dl.email ??= leerZuNull(r.email);
    dl.telefon ??= leerZuNull(r.telefon);
    dl.strasse ??= leerZuNull(r.strasse);
    dl.plz ??= leerZuNull(r.plz);
    dl.stadt ??= leerZuNull(r.stadt);
    for (const k of splitKategorien(r.kategorien)) {
      if (!dl.kategorien.includes(k)) dl.kategorien.push(k);
    }
    if (!dl.objektLinks.some(l => l.objekt_id === objektId)) {
      dl.objektLinks.push({
        objekt_id: objektId,
        von: deDateToIso(r.von),
        bis: deDateToIso(r.bis),
        vertragsart: leerZuNull(r.vertragsart),
      });
    }
  }
  return { dienstleister: Array.from(byKey.values()), ohneObjektMatch };
}

// ── Panel „CSV-Import“ (Handoff Dienstleister §7) ──────────────────────────────

/** Spalten wie der Original-Export (Spalte 12 „Saldo“ wird ignoriert, Vertragsart steht in Spalte 13). */
export const VORLAGE_KOPF = ['Objekt-Nummer', 'Beschreibung', 'Name', 'Kategorien', 'von', 'bis', 'Straße', 'PLZ', 'Stadt', 'Ansprechpartner', 'E-Mail', 'Telefon', 'Saldo', 'Vertragsart'];
export function vorlageCsv(): string {
  return `${VORLAGE_KOPF.join(';')}\r\n`;
}

/** Datei strikt als UTF-8 lesen, sonst Windows-1252 (deutsche Exporte); BOM entfernen. */
export function dekodiereCsv(buf: ArrayBuffer): string {
  let text: string;
  try { text = new TextDecoder('utf-8', { fatal: true }).decode(buf); } catch { text = new TextDecoder('windows-1252').decode(buf); }
  return text.replace(/^﻿/, '');
}

/** Wie schreibeImport: vorhanden = gleicher Name und gleiche E-Mail (bzw. beide ohne E-Mail). */
export function vorhandeneId(dl: Pick<ImportDienstleister, 'name' | 'email'>, bestehende: readonly { id: string; name: string; email: string | null }[]): string | null {
  const mail = (dl.email ?? '').trim().toLowerCase();
  return bestehende.find(b => normalizeName(b.name) === normalizeName(dl.name) && (b.email ?? '').trim().toLowerCase() === mail)?.id ?? null;
}
